import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { Button } from 'reactstrap';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';

const Recorder = forwardRef(({ 
  onRecordingComplete, 
  onRecordingStart, 
  onRecordingStop,
  maxDuration = 30000, // 30 seconds default
  enableSpeechRecognition = true,
  className = '',
  disabled = false 
}, ref) => {
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState('');
  const [recordingTime, setRecordingTime] = useState(0);
  const [isBlocked, setIsBlocked] = useState(false);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  const {
    transcript,
    listening,
    resetTranscript,
    browserSupportsSpeechRecognition
  } = useSpeechRecognition();

  // Check microphone permissions on mount
  useEffect(() => {
    checkMicrophonePermission();
    return () => {
      cleanup();
    };
  }, []);

  // Log recognized text to console when transcript changes
  useEffect(() => {
    if (transcript && enableSpeechRecognition) {
      console.log('Speech Recognition Result:', transcript);
    }
  }, [transcript, enableSpeechRecognition]);

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    startRecording,
    stopRecording
  }));

  const checkMicrophonePermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setIsBlocked(false);
      stream.getTracks().forEach(track => track.stop()); // Stop immediately after permission check
    } catch (error) {
      console.error('Microphone permission denied:', error);
      setIsBlocked(true);
    }
  };

  const startRecording = async () => {
    if (isBlocked || disabled) return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        } 
      });
      
      streamRef.current = stream;
      audioChunksRef.current = [];

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const audioUrl = URL.createObjectURL(audioBlob);
        
        setAudioBlob(audioBlob);
        setAudioUrl(audioUrl);
        
        if (onRecordingComplete) {
          onRecordingComplete({
            blob: audioBlob,
            url: audioUrl,
            transcript: transcript,
            duration: recordingTime / 1000
          });
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start speech recognition if enabled
      if (enableSpeechRecognition && browserSupportsSpeechRecognition) {
        resetTranscript();
        SpeechRecognition.startListening({ continuous: true, language: 'en-US' });
      }

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          const newTime = prev + 100;
          if (newTime >= maxDuration) {
            stopRecording();
          }
          return newTime;
        });
      }, 100);

      if (onRecordingStart) {
        onRecordingStart();
      }

    } catch (error) {
      console.error('Error starting recording:', error);
      setIsBlocked(true);
    }
  };

  const stopRecording = () => {
    if (!isRecording) return;

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }

    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    if (enableSpeechRecognition && listening) {
      SpeechRecognition.stopListening();
    }

    setIsRecording(false);

    if (onRecordingStop) {
      onRecordingStop();
    }
  };

  const cleanup = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
  };

  const formatTime = (milliseconds) => {
    const seconds = Math.floor(milliseconds / 1000);
    const ms = Math.floor((milliseconds % 1000) / 100);
    return `${seconds}.${ms}s`;
  };

  const getProgressPercentage = () => {
    return (recordingTime / maxDuration) * 100;
  };

  if (isBlocked) {
    return (
      <div className={`recorder-component ${className}`}>
        <div className="alert alert-danger">
          Microphone access is blocked. Please enable microphone permissions and reload the page.
        </div>
      </div>
    );
  }

  if (enableSpeechRecognition && !browserSupportsSpeechRecognition) {
    return (
      <div className={`recorder-component ${className}`}>
        <div className="alert alert-warning">
          Speech recognition is not supported in this browser. Recording will work without speech recognition.
        </div>
      </div>
    );
  }

  return (
    <div className={`recorder-component ${className}`}>
      <div className="recorder-controls d-flex align-items-center gap-3">
        {!isRecording ? (
          <Button
            color="success"
            onClick={startRecording}
            disabled={disabled}
            className="recorder-btn"
          >
            <i className="fa fa-microphone" aria-hidden="true"></i> Record
          </Button>
        ) : (
          <Button
            color="danger"
            onClick={stopRecording}
            className="recorder-btn"
          >
            <i className="fa fa-stop" aria-hidden="true"></i> Stop
          </Button>
        )}

        {isRecording && (
          <div className="recording-indicator d-flex align-items-center gap-2">
            <div className="recording-dot"></div>
            <span>Recording... {formatTime(recordingTime)}</span>
          </div>
        )}
      </div>

      {isRecording && (
        <div className="recording-progress mt-2">
          <div className="progress">
            <div 
              className="progress-bar bg-danger" 
              role="progressbar" 
              style={{ width: `${getProgressPercentage()}%` }}
              aria-valuenow={getProgressPercentage()}
              aria-valuemin="0" 
              aria-valuemax="100"
            ></div>
          </div>
        </div>
      )}

      {audioUrl && !isRecording && (
        <div className="recorded-audio mt-3">
          <audio controls src={audioUrl} className="w-100">
            Your browser does not support the audio element.
          </audio>
        </div>
      )}

      {enableSpeechRecognition && transcript && (
        <div className="speech-transcript mt-2">
          <small className="text-muted">
            <strong>Recognized Speech:</strong> {transcript}
          </small>
        </div>
      )}

      <style jsx>{`
        .recorder-component {
          padding: 1rem;
          border: 1px solid #dee2e6;
          border-radius: 0.375rem;
          background-color: #f8f9fa;
        }
        
        .recorder-btn {
          min-width: 100px;
        }
        
        .recording-indicator {
          color: #dc3545;
          font-weight: 500;
        }
        
        .recording-dot {
          width: 8px;
          height: 8px;
          background-color: #dc3545;
          border-radius: 50%;
          animation: pulse 1s infinite;
        }
        
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.5; }
          100% { opacity: 1; }
        }
        
        .progress {
          height: 8px;
          background-color: #e9ecef;
          border-radius: 4px;
          overflow: hidden;
        }
        
        .speech-transcript {
          padding: 0.5rem;
          background-color: #e7f3ff;
          border-radius: 0.25rem;
          border-left: 3px solid #007bff;
        }
      `}</style>
    </div>
  );
});

export default Recorder;